import * as React from "react";
import { CheckIcon, Circle, HelpCircle, LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { TableType, getAll } from "@/lib/supabase";
import useSWR from "swr";
import { useAppStore } from "@/stores";
import { getQuotaLabel } from "@/lib/app";
import { getQuotaGoalsRange, stringifyQuotaGoalsRange } from "@/quotas/components/quotas-table";
import { NestedQuota } from "@revelate/types";

type Criteria = {
  key: KeyType;
  label: string;
  type: string; // "number" | "percentage"
  icon: LucideIcon;
  description: string;
  quota_id?: number;
};

export const customQuotas: Criteria[] = [
  {
    key: {
      key_name: "custom_target",
    },
    type: "string",
    label: "Custom target",
    description: "Directly set a target for the period",
    icon: HelpCircle,
  },
];

type KeyType = {
  key_name?: string;
  quota_id?: number;
};

interface QuotaComboboxProps {
  value: KeyType | undefined;
  onChange: (value: KeyType) => void;
  onRemoveQuota: () => void;
  className?: React.HTMLProps<"div">["className"]
}

export function QuotaCombobox({
  value,
  onChange,
  onRemoveQuota,
  className,
}: QuotaComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [selectedQuota, setSelectedQuota] = React.useState<
    Criteria | undefined
  >(undefined);
  const [quota, setQuota] = React.useState<NestedQuota | undefined>(undefined);
  const currentUser = useAppStore((state) => state.currentUser);

  const [allCriterias, setAllCriterias] = React.useState<Criteria[]>([]);

  const {
    data: quotas,
    error,
    isLoading,
  } = useSWR(
    [
      "quotas" as TableType,
      currentUser ? currentUser?.company_id.toString() : null,
    ],
    ([table, companyId]) => getAll(table, companyId),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  function getQuotaFromId(id?: number) {
    if (!id) return undefined;
    const found = quotas?.find(q => q.id);
    return found;
  }

  React.useEffect(() => {
    if (!isLoading && quotas) {
      const allQuotas = quotas?.map((quota) => {
        return {
          quota_id: quota.id,
          key: {
            key_name: `quota_id_${quota.id}`,
            quota_id: quota.id,
          },
          label: `${getQuotaLabel(quota)}`,
          description: `Attained % of sales quota`,
          type: "percentage",
          icon: HelpCircle,
        };
      });
      setAllCriterias([...allQuotas, ...customQuotas]);
      const selectedCriteria = value
        ? allQuotas.find((q) => q.key.key_name === value.key_name)
        : undefined;
      setSelectedQuota(selectedCriteria);
    }
  }, [quotas, isLoading]);

  const handleSelect = (label: string) => {
    const selectedCriteria = allCriterias.find((c) => c.label === label);
    setSelectedQuota(selectedCriteria);
    setQuota(getQuotaFromId(selectedCriteria?.quota_id))
    setOpen(false);
    onChange({
      key_name: selectedCriteria?.key.key_name || "",
      quota_id: selectedCriteria?.quota_id
        ? selectedCriteria?.quota_id
        : undefined,
    });
  };

  const handleRemove = () => {
    setSelectedQuota(undefined);
    setQuota(undefined);
    setOpen(false);
    onRemoveQuota();
  }

  if (error)
    return (
      <div className="text-center text-muted-foreground">
        Error loading data
      </div>
    );

  return (
    <div className={cn("flex items-center space-x-4", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <div className="flex flex-col gap-y-1">
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="w-[350px] justify-start"
            >
              {selectedQuota ? (
                <>
                  {/* <selectedCriteria.icon className="mr-2 h-4 w-4 shrink-0" /> */}
                  {selectedQuota.label}
                </>
              ) : (
                <>No quota</>
              )}
            </Button>
          </PopoverTrigger>
          {/* {quota && ( */}
          {/*   <div className="flex flex-row gap-x-1"> */}
          {/*     <div className="hidden text-xs text-muted-foreground md:inline"> */}
          {/*       {quota.goals?.length > 0 ? ( */}
          {/*         `${stringifyQuotaGoalsRange( */}
          {/*           getQuotaGoalsRange(quota.goals) */}
          {/*         )} ${quota.target_metric === "target_number_of_deals" || quota.target_metric === "target_number_of_deals_created" ? "deals, " : quota.currency?.code} per mth, ` */}
          {/*       ) : null} */}
          {/*     </div> */}
          {/*     <div className="hidden text-xs text-muted-foreground md:inline"> */}
          {/*       {quota.goals?.length > 0 */}
          {/*         ? quota.goals?.length */}
          {/*         : "No"}{" "} */}
          {/*       sales goal */}
          {/*       {quota.goals?.length === 0 || */}
          {/*         quota.goals?.length > 1 */}
          {/*         ? "s" */}
          {/*         : ""} */}
          {/*     </div> */}
          {/*   </div> */}
          {/* )} */}
        </div>
        <PopoverContent className="p-0 w-[350px]" side="right" align="start">
          <Command>
            <CommandInput placeholder="Search quotas..." />
            <CommandList>
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  value={undefined}
                  onSelect={handleRemove}
                >
                  <div className="flex items-start gap-3 text-muted-foreground">
                    <div className="grid gap-0.5">
                      <p>
                        <span className="font-medium text-foreground">
                          No quota
                        </span>
                      </p>
                      <p className="text-xs" data-description>
                        No required targets have to be met
                      </p>
                    </div>
                  </div>
                </CommandItem>
                {allCriterias.map((criteria) => (
                  <CommandItem
                    key={criteria.key.key_name}
                    value={criteria.label}
                    onSelect={(label) => handleSelect(label)}
                  >
                    <div className="flex items-start gap-3 text-muted-foreground">
                      <div className="grid gap-0.5">
                        <p>
                          <span className="font-medium text-foreground">
                            {criteria.label}
                          </span>
                        </p>
                        <p className="text-xs" data-description>
                          {criteria.description}
                        </p>
                      </div>
                    </div>
                    <CheckIcon
                      className={cn(
                        "ml-auto my-auto h-6 w-6",
                        criteria.key.key_name === selectedQuota?.key.key_name
                          ? "opacity-100"
                          : "opacity-0"
                      )}
                    />
                  </CommandItem>
                ))}
                <CommandItem>
                  <div className="flex items-start gap-3 text-muted-foreground">
                    <div className="grid gap-0.5">
                      <p>
                        <span className="font-medium text-foreground">
                          Create new quota
                        </span>
                      </p>
                      <p className="text-xs" data-description>
                        Using the quota builder
                      </p>
                    </div>
                  </div>
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

